import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

import { useCallData } from '../../contexts/CallDataContext';
import { useWeek } from '../../contexts/WeekContext';
import { format, addDays, isWithinInterval, startOfDay, endOfDay, differenceInDays } from 'date-fns';

import { parseCustomTimestamp } from '../../utils/dateUtils';

const SuccessChart = () => {
    const { data } = useCallData();
    const { dateRange } = useWeek();

    // console.log('SuccessChart data', data);

    // This function will now return both the chart data and the period average
    const processDataForChart = () => {
        if (!dateRange || !dateRange.start || !dateRange.end) {
            return { chartData: [], weeklyAverageScore: '0.0' };
        }

        // Calculate the number of days in the selected range
        const dayCount = differenceInDays(dateRange.end, dateRange.start) + 1;
        
        // Generate dates for the selected range
        const rangeDates = Array.from({ length: dayCount }, (_, i) => 
            addDays(dateRange.start, i)
        );
        
        const dailyData = rangeDates.reduce((acc, date) => {
            acc[format(date, 'MM/dd')] = {
                totalScore: 0,
                callCount: 0
            };
            return acc;
        }, {});

        let periodTotalScore = 0;
        let periodTotalCalls = 0;

        // Sum up success scores for each day
        data.forEach(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (callDate) {
                const rangeStart = startOfDay(dateRange.start);
                const rangeEnd = endOfDay(dateRange.end);
                
                if (isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                    // Check for success_score in different locations with proper fallback
                    let score = 0;
                    if (call.success_score !== undefined && call.success_score !== null) {
                        score = call.success_score;
                    } else if (call.evaluation?.success_score !== undefined && call.evaluation?.success_score !== null) {
                        score = call.evaluation.success_score;
                    }

                    // Add to daily totals
                    const formattedDate = format(callDate, 'MM/dd', { timeZone: 'UTC' });
                    if (dailyData.hasOwnProperty(formattedDate)) {
                        dailyData[formattedDate].totalScore += score;
                        dailyData[formattedDate].callCount++;
                    }

                    // Add to period totals for the top display calculation
                    periodTotalScore += score;
                    periodTotalCalls++;
                }
            }
        });

        // Calculate average score PER DAY for the chart
        const chartData = rangeDates.map(date => {
            const formattedDate = format(date, 'MM/dd');
            const dayData = dailyData[formattedDate];
            const score = dayData.callCount > 0 
                ? +(dayData.totalScore / dayData.callCount).toFixed(1)
                : 0;
            return {
                date: formattedDate,
                score
            };
        });

        // Calculate overall average score PER CALL for the period
        const weeklyAverageScore = periodTotalCalls > 0 
            ? (periodTotalScore / periodTotalCalls).toFixed(1) 
            : '0.0';

        return { chartData, weeklyAverageScore };
    };

    const { chartData, weeklyAverageScore } = processDataForChart();

    return (
        <div className="flex flex-col items-center card-container w-full h-[300px] space-y-2">
            <div className='flex flex-row w-full items-center justify-between'>
                <div className="text-sm text-font-primary mb-2">Success Evaluation</div>
                <span className='text-lg font-semibold'>{weeklyAverageScore}</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -5 }}>
                    <XAxis dataKey="date" stroke="#aaa" />
                    <YAxis stroke="#aaa" />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#60992D"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default SuccessChart;
  
